/**
 * ErrorBoundary (Feature G1)
 * ==========================
 * Catches render-time errors anywhere in the React tree and shows a
 * friendly fallback UI instead of a blank white screen.
 *
 * Why a class component? Because that's the only way React exposes
 * `componentDidCatch` / `getDerivedStateFromError`.
 */
import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

export default class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Developer console breadcrumb
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] React render error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || 'Unexpected error';

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 space-y-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
          </div>

          <p className="text-sm text-slate-600">
            The page hit a rendering error. Your data is safe — this only affects the current view.
          </p>

          <details className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
            <summary className="cursor-pointer font-medium text-slate-700 mb-2">Technical details</summary>
            <pre className="whitespace-pre-wrap break-words">{message}</pre>
          </details>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={this.handleReset} data-testid="error-boundary-retry">
              Try again
            </Button>
            <Button onClick={this.handleReload} data-testid="error-boundary-reload">
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
